import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchAppointments,
  fetchQueueStats,
  fetchDoctorSchedule,
} from "../../redux/Thunk/Appointment/AppointmentThunks";
import { setSelectedDate } from "../../redux/Reducer/Appointment/AppointmentSlice";
import { formatDateString, getTodayString } from "../../utils/helpers";
import SidebarToggle from "../../components/Doctor/SidebarToggle";
import LiveQueueMonitor from "../../components/Doctor/Planner/LiveQueueMonitor";
import CalendarTimelineView from "../../components/Doctor/Planner/CalendarTimelineView";
import MonthlyCalendarView from "../../components/Doctor/Planner/MonthlyCalendarView";
import AppointmentsTableView from "../../components/Doctor/Planner/AppointmentsTableView";
import PlannerReceiptMaker from "../../components/Doctor/Planner/PlannerReceiptMaker";
import DoctorSlotConfigPanel from "../../components/Doctor/Planner/DoctorSlotConfigPanel";
import NewAppointmentModal from "../../components/Doctor/Planner/NewAppointmentModal";

const ChevronLeftIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

// Clean Outlined Minimal Icons
const QueueIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TableIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2zm0 4h18M9 7v12M15 7v12" />
  </svg>
);

const ReceiptIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const Planner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isPinB = useSelector((state) => state.auth?.user?.isPinB);
  const selectedDate = useSelector((state) => state.appointment.selectedDate);
  const appointments = useSelector((state) => state.appointment.appointments);
  const queueStats = useSelector((state) => state.appointment.queueStats);

  const [activeTab, setActiveTab] = useState("QUEUE"); // 'QUEUE' | 'MONTHLY' | 'CALENDAR' | 'TABLE' | 'RECEIPTS' | 'SETTINGS'
  const [isBookModalOpen, setIsBookModalOpen] = useState(false);
  const [selectedSlotForBooking, setSelectedSlotForBooking] = useState("");
  const [receiptAppointment, setReceiptAppointment] = useState(null);

  const handleOpenHealthMap = (appointment) => {
    if (!appointment) return;
    const patientContext = {
      PID: appointment.PID || `PID-${appointment.patientPhone}`,
      name: appointment.patientName,
      phoneNumber: appointment.patientPhone,
      gender: appointment.patientGender || "Male",
      age: appointment.patientAge,
      city: appointment.city,
      pincode: appointment.pincode,
    };
    sessionStorage.setItem("healthmap-patient-context", JSON.stringify(patientContext));
    navigate("/healthmap");
  };

  // Initial load & date/tab change listener
  useEffect(() => {
    if (activeTab !== "MONTHLY") {
      dispatch(fetchAppointments({ date: selectedDate }));
      dispatch(fetchQueueStats(selectedDate));
    }
    dispatch(fetchDoctorSchedule());
  }, [dispatch, selectedDate, activeTab]);

  // Real-time live polling every 6 seconds across single-day views
  useEffect(() => {
    if (activeTab === "QUEUE" || activeTab === "CALENDAR" || activeTab === "TABLE") {
      const interval = setInterval(() => {
        dispatch(fetchAppointments({ date: selectedDate }));
        dispatch(fetchQueueStats(selectedDate));
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [dispatch, selectedDate, activeTab]);

  const isToday = (selectedDate || getTodayString()) === getTodayString();

  const handleDateChange = (e) => {
    dispatch(setSelectedDate(e.target.value));
  };

  const handleTodayInHeader = () => {
    dispatch(setSelectedDate(getTodayString()));
  };

  const handlePrevDateInHeader = () => {
    const parts = (selectedDate || getTodayString()).split("-").map(Number);
    const dt = new Date(parts[0], parts[1] - 1, parts[2] - 1);
    dispatch(setSelectedDate(formatDateString(dt)));
  };

  const handleNextDateInHeader = () => {
    const parts = (selectedDate || getTodayString()).split("-").map(Number);
    const dt = new Date(parts[0], parts[1] - 1, parts[2] + 1);
    dispatch(setSelectedDate(formatDateString(dt)));
  };

  const handleOpenReceiptForAppointment = (appointment) => {
    setReceiptAppointment(appointment);
    setActiveTab("RECEIPTS");
  };

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Fluid & Fully Responsive Top Header Bar */}
      <div className="bg-white p-3.5 sm:p-4 md:p-5 rounded-3xl border border-gray-100 shadow-sm flex flex-wrap items-center justify-between gap-x-4 md:gap-x-6 gap-y-3">
        {/* Brand & Sidebar Toggle */}
        <div className="flex items-center gap-3 shrink-0">
          <SidebarToggle className="!p-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl" />

          {/* Brand Logo & Wordmark + Page Name */}
          <div className="flex flex-col gap-1 shrink-0 justify-center">
            <div className="flex items-center gap-1.5 shrink-0">
              <img src="/logo.png" alt="logo" className="h-[18px] sm:h-[22px] w-auto object-contain shrink-0" />
              <img src="/logo.svg" alt="Hyperapt" className="h-[15px] sm:h-[18px] w-auto max-w-[90px] sm:max-w-[105px] object-contain shrink-0" />
            </div>
            <span className="text-[17px] sm:text-[21px] font-black text-gray-900 tracking-tight leading-none">
              OPD Planner
            </span>
          </div>

          {/* Desktop Partition Divider */}
          <div className="h-7 w-[1px] bg-gray-200 hidden md:block mx-1"></div>
        </div>

        {/* Interactive Date Selector */}
        <div className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-1.5 bg-gray-50/90 p-1.5 rounded-2xl border border-gray-200/80 shadow-2xs">
          <div className="flex items-center gap-1 flex-1 sm:flex-initial justify-between sm:justify-start">
            <button
              type="button"
              onClick={handlePrevDateInHeader}
              className="p-1.5 hover:bg-white text-gray-600 hover:text-gray-900 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center shrink-0"
              title="Previous Day"
            >
              <ChevronLeftIcon />
            </button>

            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-xl border border-gray-200/60 shadow-2xs justify-center flex-1 sm:flex-initial min-w-0">
              <CalendarIcon />
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="bg-transparent text-xs font-black text-gray-900 outline-none cursor-pointer text-center sm:text-left min-w-[110px]"
              />
            </div>

            <button
              type="button"
              onClick={handleNextDateInHeader}
              className="p-1.5 hover:bg-white text-gray-600 hover:text-gray-900 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center shrink-0"
              title="Next Day"
            >
              <ChevronRightIcon />
            </button>
          </div>

          {/* Today Quick Button */}
          <button
            type="button"
            onClick={handleTodayInHeader}
            className={`px-3 py-1 rounded-xl text-xs font-extrabold transition-all flex items-center gap-1.5 cursor-pointer border shadow-2xs active:scale-95 shrink-0 ${
              isToday
                ? "bg-emerald-50 text-emerald-800 border-emerald-200/80 hover:bg-emerald-100"
                : "bg-amber-50 text-amber-900 border-amber-200/80 hover:bg-amber-100"
            }`}
            title={isToday ? "Viewing Today's OPD Queue" : "Click to return to Today's date"}
          >
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                isToday ? "bg-emerald-500 shadow-2xs shadow-emerald-500/50" : "bg-amber-500 shadow-2xs shadow-amber-500/50 animate-pulse"
              }`}
            />
            <span>Today</span>
          </button>
        </div>

        {/* Book Appointment Button */}
        <div className="w-full sm:w-auto flex items-center justify-center">
          <button
            type="button"
            onClick={() => {
              setSelectedSlotForBooking("");
              setIsBookModalOpen(true);
            }}
            className="w-full sm:w-auto px-4 py-2 sm:py-1.5 rounded-xl bg-red-50 hover:bg-red-600 text-red-700 hover:text-white border border-red-200/80 hover:border-red-600 text-xs font-bold transition-all shadow-2xs active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer shrink-0"
            title="Book New Appointment"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            <span>Book Appointment</span>
          </button>
        </div>
      </div>

      {/* 2. Minimal & Mobile-Friendly Segmented Tab Bar */}
      <div className="bg-gray-100/80 p-1.5 rounded-2xl border border-gray-200/60 flex items-center gap-1 overflow-x-auto no-scrollbar scroll-smooth">
        <button
          type="button"
          onClick={() => setActiveTab("QUEUE")}
          className={`px-3.5 py-2 rounded-xl text-xs transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
            activeTab === "QUEUE"
              ? "bg-white text-gray-900 font-bold shadow-xs border border-gray-200/80"
              : "text-gray-600 hover:text-gray-900 font-medium hover:bg-white/50"
          }`}
        >
          <QueueIcon />
          <span>Live OPD Queue</span>
          {queueStats.waitingCount > 0 && (
            <span className="px-1.5 py-0.2 rounded-full bg-amber-400 text-gray-900 text-[10px] font-black">
              {queueStats.waitingCount}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("MONTHLY")}
          className={`px-3.5 py-2 rounded-xl text-xs transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
            activeTab === "MONTHLY"
              ? "bg-white text-gray-900 font-bold shadow-xs border border-gray-200/80"
              : "text-gray-600 hover:text-gray-900 font-medium hover:bg-white/50"
          }`}
        >
          <CalendarIcon />
          <span>Monthly Calendar</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("CALENDAR")}
          className={`px-3.5 py-2 rounded-xl text-xs transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
            activeTab === "CALENDAR"
              ? "bg-white text-gray-900 font-bold shadow-xs border border-gray-200/80"
              : "text-gray-600 hover:text-gray-900 font-medium hover:bg-white/50"
          }`}
        >
          <ClockIcon />
          <span>Schedule Timeline</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("TABLE")}
          className={`px-3.5 py-2 rounded-xl text-xs transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
            activeTab === "TABLE"
              ? "bg-white text-gray-900 font-bold shadow-xs border border-gray-200/80"
              : "text-gray-600 hover:text-gray-900 font-medium hover:bg-white/50"
          }`}
        >
          <TableIcon />
          <span>Master Table</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("RECEIPTS")}
          className={`px-3.5 py-2 rounded-xl text-xs transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
            activeTab === "RECEIPTS"
              ? "bg-white text-gray-900 font-bold shadow-xs border border-gray-200/80"
              : "text-gray-600 hover:text-gray-900 font-medium hover:bg-white/50"
          }`}
        >
          <ReceiptIcon />
          <span>Receipt Maker</span>
        </button>

        {isPinB && (
          <button
            type="button"
            onClick={() => setActiveTab("SETTINGS")}
            className={`px-3.5 py-2 rounded-xl text-xs transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
              activeTab === "SETTINGS"
                ? "bg-white text-gray-900 font-bold shadow-xs border border-gray-200/80"
                : "text-gray-600 hover:text-gray-900 font-medium hover:bg-white/50"
            }`}
          >
            <SettingsIcon />
            <span>Shift Settings</span>
          </button>
        )}
      </div>

      {/* 3. Tab Content View */}
      {activeTab === "QUEUE" && (
        <LiveQueueMonitor
          appointments={appointments}
          queueStats={queueStats}
          isPinB={isPinB}
          onOpenReceiptMaker={handleOpenReceiptForAppointment}
        />
      )}

      {activeTab === "MONTHLY" && (
        <MonthlyCalendarView
          onSelectDate={(dateStr) => {
            setActiveTab("QUEUE");
          }}
          onSelectAppointment={(app) => {
            if (isPinB) {
              handleOpenHealthMap(app);
            } else {
              handleOpenReceiptForAppointment(app);
            }
          }}
          onNewAppointmentForDate={(dateStr) => {
            setSelectedSlotForBooking("");
            setIsBookModalOpen(true);
          }}
        />
      )}

      {activeTab === "CALENDAR" && (
        <CalendarTimelineView
          appointments={appointments}
          selectedDate={selectedDate}
          onSelectTimeSlot={(slot) => {
            setSelectedSlotForBooking(slot);
            setIsBookModalOpen(true);
          }}
        />
      )}

      {activeTab === "TABLE" && (
        <AppointmentsTableView
          appointments={appointments}
          selectedDate={selectedDate}
          onOpenReceiptMaker={handleOpenReceiptForAppointment}
        />
      )}

      {activeTab === "RECEIPTS" && (
        <PlannerReceiptMaker
          selectedAppointment={receiptAppointment}
          isPinB={isPinB}
          onClose={() => {
            setReceiptAppointment(null);
            setActiveTab("QUEUE");
          }}
        />
      )}

      {activeTab === "SETTINGS" && isPinB && (
        <DoctorSlotConfigPanel />
      )}

      {/* 4. Modals */}
      <NewAppointmentModal
        isOpen={isBookModalOpen}
        onClose={() => setIsBookModalOpen(false)}
        initialDate={selectedDate}
        initialTimeSlot={selectedSlotForBooking}
      />
    </div>
  );
};

export default Planner;
